// 환생 재육성 속도(C12, PROGRESSION-720H 3절): 환생한 영웅이 ARPG 필드에서 Lv1→99를 다시 오르는 시간 = 1회차 같은 레벨 구간 시간 × 비율.
// 디아블로식 "둘째 캐릭터는 3~4배 빠르다": 1차 환생 0.30, 2차 0.25. 전에는 레벨과 무관하게 '120마리 = 1레벨'(1→99 약 5시간)이라
// 1회차 214시간을 들인 뒤 재육성이 사실상 건너뛰기였다 — 대균열 상한(레벨/5)·레벨 동기화 장비·필드 이벤트가 이 시간을 채운다.
// 표 FIRST_RUN_LEVEL_MIN = 720h 시뮬(designed, 시드 1, 해시 f4a515894428a20d) 카엘 1회차의 레벨 L→L+1 분(진행 시간, 하루 루틴 포함).
// 다시 만들기: bun tools/sim/relevelTable.ts (relevel.test.ts가 시뮬과 같은지 확인한다).

/** 1회차 레벨 L→L+1 분(L = 1..98, 0번은 비움) */
export const FIRST_RUN_LEVEL_MIN: readonly number[] = [0,9.4,7.7,9.1,10.3,10.3,8.5,9.4,9.7,8.8,10.4,17.9,8,17.2,17.1,16.7,18.2,16,18.1,26.6,18.7,17.9,18.6,28.5,17.9,25.7,17.8,25.7,27.2,28.4,51.8,27.7,28,27.8,27.2,34.8,36,35.1,36.1,25.8,34,35.6,36.9,36.2,80.5,37.3,37.3,33.9,37.2,34.3,34.9,37.6,45.7,34.9,35.3,36.1,85.5,50.5,63.4,63.6,79,78,167.4,97.8,111.6,106.5,158,99.1,106.3,111.8,182,104.6,113.2,132.2,209.9,175.5,241.6,237.7,545.4,253.3,251.3,331.3,367.3,400.1,424.3,396.9,436.1,466.1,434.2,427.8,418,412.4,432.3,448,449.9,415.9,474.1,404.7,428.9];

/** 회차별 재육성 시간 비율(시뮬 ASSUME720.relevelShare와 같은 값) */
export const RELEVEL_SHARE = { rebirth1: 0.3, rebirth2: 0.25, alt: 0.35 } as const;
/** 필드 분당 처치(PC 실입력 표본, 장부 arpg-relevel calc — 시뮬 ASSUME720.fieldKillsPerMinute) */
export const FIELD_KILLS_PER_MINUTE = 27.1;
/** 옛 규칙의 바닥(레벨당 최소 처치 수): 표의 저레벨 구간이 너무 짧아지지 않게 */
export const RELEVEL_MIN_KILLS = 120;

export function relevelShare(cycle: number): number { return cycle >= 2 ? RELEVEL_SHARE.rebirth2 : RELEVEL_SHARE.rebirth1; }

/**
 * 필드에서 레벨 L→L+1에 필요한 처치 수(고블린 기준). 환생 EXP +50%가 grantXp에서 따로 곱해지므로 그만큼 늘려 둔다:
 * 처치 수 = 1회차 분 × 비율 × 분당 처치 × (1 + 환생 EXP 보너스). 실제 시간 = 처치 수 ÷ 1.5 ÷ 분당 처치 = 1회차 분 × 비율.
 */
export function fieldKillsPerLevel(level: number, cycle: number, rebirthXpBonus = 0.5): number {
  const L = Math.max(1, Math.min(FIRST_RUN_LEVEL_MIN.length - 1, Math.floor(level)));
  const kills = FIRST_RUN_LEVEL_MIN[L] * relevelShare(cycle) * FIELD_KILLS_PER_MINUTE * (1 + rebirthXpBonus);
  return Math.max(RELEVEL_MIN_KILLS, Math.round(kills));
}

/**
 * 다른 영웅 계정 보너스(PROGRESSION-720H 3절, 디아블로 '둘째 캐릭터'·로아 '원정대' 결): 이 캐릭터의 다른 영웅이 한 번이라도 Lv99에 닿았으면
 * (또는 환생했으면) 아직 환생하지 않은 영웅의 EXP = ×1/0.35(≈2.86) — 1→99가 첫 영웅 시간의 35%. 창고·골드·숙련 공유와 같은 '계정 진척' 몫이다.
 * 환생한 영웅(회차 ≥ 1)은 필드 재육성 표가 따로 정하므로 겹치지 않는다.
 */
export const ALT_XP_MULT = Math.round((1 / RELEVEL_SHARE.alt) * 100) / 100;
export function altHeroXpMult(heroCycle: number, otherHeroMaxed: boolean): number {
  return heroCycle === 0 && otherHeroMaxed ? ALT_XP_MULT : 1;
}
